import {
  EpkModuleApiService,
  getFilteredData,
} from '~packages/epk-front-shared/api-client';
import type { Filters, Params } from '~packages/epk-front-shared/api-client';
import { Mode, RequestMethod } from '~packages/json-api/client';

import getConstants from '@/shared/constants';

import {
  IFetchDataQuery,
  IFetchDataRoute,
} from '../types/EpkReportsApiService';

const { isModeDevelopment } = getConstants();

const constructorParams = {
  className: 'EpkReportsApiService',
  mode: isModeDevelopment ? Mode.Production : Mode.Production,
  // needInit: isModeDevelopment,
  pathPrint: '/prints/v1',
};

export default class EpkReportsApiService extends EpkModuleApiService {
  constructor() {
    super(constructorParams);
  }

  async fetchData(
    { cacheGuid, scope }: IFetchDataRoute,
    { responseFormat }: IFetchDataQuery = {}
  ) {
    let str = `data/${scope}/${cacheGuid}`;
    if (responseFormat) {
      str += '?response_format=' + responseFormat;
    }
    return await super.fetch(str);
  }

  async getAllBooks(params?: any) {
    return await super.fetchAll(params);
  }

  async getBookById(id) {
    return await super.fetch('doc/' + id);
  }

  async addBookById(params) {
    return await super.add(params);
  }

  async getAllOktmos(path) {
    return await super.fetch(path);
  }

  async getFilteredData(filters: Filters, params: Params) {
    return await getFilteredData(this, filters, params);
  }

  async getTitleInfo({
    epk,
    periodStart,
    periodEnd,
    cacheGuid,
    timeStamp,
  }: {
    epk: string;
    periodStart: string;
    periodEnd: string;
    cacheGuid: string;
    timeStamp: string;
  }) {
    return await super.fetch(
      `data/title/${epk}/${periodStart}/${periodEnd}/${cacheGuid}/${timeStamp}`
    );
  }

  async getTableInfo({
    route,
    uuid,
    timeStamp,
  }: {
    route: string;
    uuid: string;
    timeStamp: string;
  }) {
    return await super.fetch(`data/${route}/${uuid}/${timeStamp}`);
  }

  async dataGet(
    {
      route,
      uuid,
      timeStamp,
    }: { route: string; uuid: string; timeStamp: string },
    params?: Record<string, unknown>
  ) {
    return await this._sendRequest(
      `/data/${route}/${uuid}/${timeStamp}`,
      params,
      undefined,
      {
        method: 'dataGet',
      }
    );
  }

  async getRefreshInfo({
    point_code,
    tables,
  }: {
    point_code: string;
    tables: object;
  }) {
    return await super.update({ tables }, `${point_code}/refresh`);
  }

  async approveBookOneYear(point_code: string, url: string) {
    const path = `/${point_code}/one-year/commit-eds`;
    const params = {};
    const data = { url };
    const response = await super._sendRequest(
      path,
      params,
      { body: JSON.stringify(data), method: RequestMethod.PUT },
      { method: 'upload' }
    );
    return response;
  }

  async approveBookOneYearRPC(point_code: string, url: string) {
    const path = `/${point_code}/one-year/commit-eds`;
    const params = {};
    const data = { url };
    const response = await super._sendRequest(
      path,
      params,
      { body: JSON.stringify(data), method: RequestMethod.PUT },
      { method: 'upload', options: { headers: { 'rpc-execute': true } } }
    );
    return response;
  }

  async rollbackBook(point_code: string) {
    return await super.update({}, `${point_code}/drop-commit-eds`);
  }

  async rollbackBookRPC(point_code: string) {
    return await this._sendRequest(
      `/${point_code}/drop-commit-eds`,
      undefined,
      { method: RequestMethod.PUT },
      {
        method: 'rollbackBookRPC',
        options: { headers: { 'rpc-execute': true } },
      }
    );
  }

  async addEpkRPC(data: unknown, params?: Record<string, unknown>) {
    return await this._sendRequest(
      this.pathAdd,
      params,
      { body: this.jsonStringify(data), method: this.requestMethodAdd },
      { method: 'addEpkRPC', options: { headers: { 'rpc-execute': true } } }
    );
  }

  async print(cacheGuid: string, params: Record<string, unknown>) {
    return await this._sendRequest(
      `${constructorParams.pathPrint}/${cacheGuid}`,
      params,
      { method: RequestMethod.POST },
      { method: 'print' }
    );
  }

  async printRPC(cacheGuid: string) {
    return await this._sendRequest(
      `${constructorParams.pathPrint}/${cacheGuid}`,
      undefined,
      { method: RequestMethod.POST },
      {
        method: 'printRPC',
        options: { headers: { 'rpc-execute': true } },
      }
    );
  }

  async getRefreshInfoPRC(point_code: string) {
    return await this._sendRequest(
      `/${point_code}/refresh`,
      undefined,
      { method: RequestMethod.PUT },
      {
        method: 'getRefreshInfoPRC',
        options: { headers: { 'rpc-execute': true } },
      }
    );
  }

  async deleteByCode(code: string) {
    return await this._sendRequest(
      `/${code}`,
      undefined,
      { method: RequestMethod.DELETE },
      { method: 'delete' }
    );
  }

  /* Свод за 5 лет */

  async approveBookFiveYears(point_code: string, url: string) {
    const path = `/${point_code}/five-years/commit-eds`;
    const params = {};
    const data = { url };
    const response = await super._sendRequest(
      path,
      params,
      { body: JSON.stringify(data), method: RequestMethod.PUT },
      { method: 'upload' }
    );
    return response;
  }

  async approveBookFiveYearsRPC(dim_epk_code: string, url: string) {
    const path = `/${dim_epk_code}/five-years/commit-eds`;
    const params = {};
    const data = { url };
    const response = await super._sendRequest(
      path,
      params,
      { body: JSON.stringify(data), method: RequestMethod.PUT },
      { method: 'upload', options: { headers: { 'rpc-execute': true } } }
    );
    return response;
  }

  async rollbackBookFiveRPC(dim_epk_code: string) {
    return await this._sendRequest(
      `/${dim_epk_code}/five-years/drop-commit-eds`,
      undefined,
      { method: RequestMethod.PUT },
      {
        method: 'rollbackBookFiveRPC',
        options: { headers: { 'rpc-execute': true } },
      }
    );
  }

  async printFiveRPC(epk_num: string) {
    return await this._sendRequest(
      `${constructorParams.pathPrint}/five_years/${epk_num}`,
      undefined,
      { method: RequestMethod.POST },
      {
        method: 'printFiveRPC',
        options: { headers: { 'rpc-execute': true } },
      }
    );
  }

  async getRefreshInfoFivePRC(dim_epk_code: string) {
    return await this._sendRequest(
      `/${dim_epk_code}/five-years/refresh`,
      undefined,
      { method: RequestMethod.PUT },
      {
        method: 'getRefreshInfoFivePRC',
        options: { headers: { 'rpc-execute': true } },
      }
    );
  }
}
